import { LoginOption, SOCIAL_AUTH_PROVIDERS } from 'constant/sso';
import { GRANT_TYPES } from 'constant/auth';
import SSOSigninCard from 'features/auth/components/sso-signin-card/sso-signin-card';
import { useTranslation } from 'react-i18next';
import { AuthDivider } from '../shared/auth-divider';
import { Button } from 'components/ui/button';
import { SsoService } from 'features/auth/services/sso-service';
import { useConfig } from 'hooks/use-config';
import { cn } from 'lib/utils';
import { useEffect, useState } from 'react';

type SsoSigninProps = {
  loginOption: LoginOption;
  variant?: 'default' | 'klax';
  showDivider?: boolean;
};

export const SsoSignin = ({ loginOption, variant = 'default', showDivider = true }: SsoSigninProps) => {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const socialGrantAllowed = loginOption?.allowedGrantTypes?.includes(GRANT_TYPES.social);
  const isKlax = variant === 'klax';
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Browser Back from Microsoft/SSO can restore this page via bfcache with isRedirecting still true.
  useEffect(() => {
    const onPageShow = () => setIsRedirecting(false);
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  if (!socialGrantAllowed) {
    return null;
  }

  const allProviders = Object.values(SOCIAL_AUTH_PROVIDERS).map((provider) => {
    const ssoInfo = loginOption.ssoInfo?.find((s) => s.provider === provider.value);

    return {
      ...provider,
      audience: ssoInfo?.audience ?? '',
      provider: ssoInfo?.provider ?? provider.value,
      isAvailable: !!ssoInfo,
      clientId: ssoInfo?.clientId ?? '',
      redirectUris: ssoInfo?.redirectUris ?? [],
      icon: ssoInfo?.icon ?? '',
      displayName: ssoInfo?.displayName ?? '',
    };
  });

  const providersToShow = allProviders.filter((provider) => provider.isAvailable);

  if (providersToShow.length === 0) {
    return null;
  }

  const isSingleProvider = providersToShow.length === 1;

  if (isKlax && isSingleProvider) {
    const provider = providersToShow[0];
    const ssoService = new SsoService();

    const onSsoClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!provider.isAvailable) return;

      setIsRedirecting(true);

      const res = await ssoService.getSocialLoginEndpoint(
        {
          clientId: provider.clientId,
          audience: provider.audience,
          sendAsResponse: true,
          redirectUri: window.location.origin + '/login',
        },
        configurationSettings
      );

      if (res.error) {
        setIsRedirecting(false);
        return alert(`Authentication error: ${res.error}`);
      }
      if (!res?.authorizationUrl) {
        setIsRedirecting(false);
        return alert(t('NO_REDIRECT_URL_RECEIVED', 'No redirect URL received from the authentication service.') );
      }

      localStorage.setItem('sso_provider', provider.provider);
      window.location.href = res.authorizationUrl;
    };

    return (
      <>
        <Button
          type="button"
          variant="outline"
          onClick={onSsoClick}
          disabled={!provider.isAvailable || isRedirecting}
          className={cn(
            'flex h-[50px] w-full items-center justify-center gap-2 rounded-full border-[#d1d1d1] bg-white px-6 py-3 text-sm font-medium leading-[1.2] text-black shadow-none sm:h-[54px] sm:gap-[12px] sm:px-10 sm:py-3.5 sm:text-base',
            'hover:bg-[#f5f5f5] hover:text-black',
            'dark:!border-true-white dark:!bg-transparent dark:!text-true-white dark:hover:!bg-white/10 dark:hover:!text-true-white'
          )}
        >
          {isRedirecting ? (
            t('LOGGING_YOU_IN', 'Logging you in...')
          ) : (
            <>
              <img src={provider.imageSrc} width={20} height={20} alt="" />
              {t('LOGIN_WITH', { provider_name: provider.displayName.charAt(0).toUpperCase() + provider.displayName.slice(1).toLowerCase() })}
            </>
          )}
        </Button>

        {showDivider && (
          <div className={cn(!isKlax && 'mt-4')}>
            <AuthDivider text={t('AUTH_OR')} variant={variant} />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {showDivider && (
        <div className={cn(!isKlax && 'mt-4')}>
          <AuthDivider text={t('AUTH_OR')} variant={variant} />
        </div>
      )}

      <div className={cn('flex w-full items-center', isSingleProvider ? 'justify-center' : 'gap-4')}>
        {providersToShow.map((item) => (
          <SSOSigninCard
            key={item?.value}
            providerConfig={item}
            showText={isSingleProvider}
            totalProviders={providersToShow.length}
          />
        ))}
      </div>
    </>
  );
};
